import React, { useContext } from 'react';
import {
  Box,
  Button,
  Container,
  CssBaseline,
  Typography,
  Paper,
  Fade,
} from '@mui/material';
import { useLocation, useNavigate } from "react-router-dom";
import { BASE_URL, Notification_Url } from "../Api/ApiUrls";
import { AuthContext } from './../AuthContext/AuthContext';
import { generateToken } from './../Firebase/Firebase';
import landNestLogo from './../../Components/assets/LandNestLogo.jpg';

const ForceLoginPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const { identifier, fcmToken, rememberMe } = location.state || {};

  const handleContinue = async () => {
    try {
      // Ensure we still have a token
      const finalFcmToken = fcmToken || (await generateToken());

      const response = await fetch(`${BASE_URL}/force-login/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          identifier: identifier,
          fcm_token: finalFcmToken || "",
        }),
      });

      const data = await response.json();

      if (response.ok) {
        console.log("Force login success:", data);

        login(data.user_id, data.session_token);

        if (rememberMe) {
          sessionStorage.setItem('isAuthenticated', 'true');
          sessionStorage.setItem('user_id', data.user_id);
          sessionStorage.setItem('session_token', data.session_token);
        }

        if (finalFcmToken) {
          try {
            const notifyResponse = await fetch(`${Notification_Url}/send-notification`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                token: finalFcmToken,
                title: "Welcome to LandNest!",
                body: "You have successfully logged in.",
              }),
            });

            const notifyData = await notifyResponse.json();
            if (notifyResponse.ok) {
              console.log("Notification sent successfully:", notifyData);
            } else {
              console.error("Failed to send notification:", notifyData);
            }
          } catch (notifyError) {
            console.error("Error sending notification:", notifyError);
          }
        } else {
          console.warn("No FCM token available for notification.");
        }

        navigate("/dashboard");
      } else {
        console.error("Force login failed:", data);
      }
    } catch (err) {
      console.error("Error in force login:", err);
    }
  };

  return (
    <Fade in timeout={700}>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0d0d25, #000)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
        }}
      >
        <Container maxWidth="sm">
          <CssBaseline />
          <Paper
            elevation={10}
            sx={{
              p: 4,
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 215, 0, 0.3)',
              backdropFilter: 'blur(10px)',
              textAlign: 'center',
              color: '#fff',
            }}
          >
            <Box
              component="img"
              src={landNestLogo}
              alt="Land Nest Logo"
              sx={{
                width: { xs: 120, sm: 180 },
                height: { xs: 80, sm: 120 },
                mb: 2,
                mx: 'auto',
                borderRadius: 2,
                boxShadow: '0 0 20px #ffd700',
                display: 'block',
              }}
            />

            <Typography 
              variant="h5" 
              sx={{ 
                color: '#f7e896', 
                fontWeight: 600, 
                mb: 2,
                fontSize: { xs: '1.3rem', sm: '1.5rem' }
              }}
            >
              Already Logged In Elsewhere
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#ccc', 
                mb: 4,
                fontSize: { xs: '0.9rem', sm: '1rem' }
              }}
            >
              You are already logged in on another device. Do you want to logout from that device and continue here?
            </Typography>

            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' }, 
              justifyContent: 'center',
              gap: 2,
              mt: 3
            }}>
              <Button
                onClick={handleContinue}
                variant="contained"
                sx={{
                  background: 'linear-gradient(135deg, #f1e57c, #e6ce45)',
                  color: '#000',
                  fontWeight: 'bold',
                  px: 4,
                  py: 1,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #ffe85c, #f0d53e)',
                  },
                }}
              >
                Continue Here
              </Button>
              
              <Button
                onClick={() => navigate("/")}
                variant="outlined"
                sx={{
                  color: '#f7e896',
                  borderColor: 'rgba(255, 215, 0, 0.5)',
                  px: 4,
                  py: 1,
                  '&:hover': {
                    borderColor: '#ffd700',
                    backgroundColor: 'rgba(255, 215, 0, 0.1)'
                  },
                }}
              >
                Go to Login
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    </Fade>
  );
};

export default ForceLoginPage;